import { DialogComponent } from '@dialog/dialog.component';
import { ICompany } from '@company/company.structures';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import {
  MatTableDataSource, MatSnackBar, MatSidenav, MatDialog,
  MatPaginator, MatSort
} from '@angular/material';
import { Router } from '@angular/router';
import { Subscription, Observable } from 'rxjs';

import { CompanyService } from '@company/company.service';
import { ExceptionService } from '@exceptions-service';
import { IErrorResponse, IDialogData, IUser, IUserCompany } from '@interfaces';
import { AppService } from '@base/app.service';
import { UserService } from '@user/user.service';
import { ROLES } from '@utils';
import { MediaMatcher } from '@angular/cdk/layout';

@Component({
  selector: 'app-company-list',
  templateUrl: './company-list.component.html',
  styleUrls: ['./company-list.component.sass']
})
export class CompanyListComponent implements OnInit, OnDestroy {
  ROLES = ROLES;
  private currentUser: IUser = null;
  private observable: Observable<any>;
  private listSubscriptions: Subscription[] = [];
  private _mobileQueryListener: () => void;

  mobileQuery: MediaQueryList;
  displayedColumns: string[] = ['name', 'details', 'actions', ];
  dataSource: MatTableDataSource<ICompany>;
  rows: ICompany[];
  updatingStatus = false;
  gettingList: Observable<boolean>;
  company: Partial<ICompany>;
  users: IUser[][];

  // MatPaginator Inputs
  length = 0;
  pageSize = 5;
  pageSizeOptions: number[] = [5, ];

  @ViewChild ('companysidenav') public sidenav: MatSidenav;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private router: Router,
    public dialog: MatDialog,
    private companyService: CompanyService,
    public snackBar: MatSnackBar,
    private exceptionService: ExceptionService,
    private mainService: AppService,
    private userService: UserService,
  ) {
    this.gettingList = this.companyService.observeBusyStatus();
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    this.company = {
      name: '',
      slug: '',
      domain: '',
    };
    this.users = [];
    this.initializeDataset();
    this.fetchData();

    this.observable = this.companyService.observeCompanies();
    const companiesSubscription = this.observable.subscribe(
      (response: ICompany[]) => this.dataAdapter(response),
      (err: HttpErrorResponse) => {
        this.openSnackBar(err.statusText, err.error);
      }
    );

    const usersSubscription = this.userService.observeUsers().subscribe(
      (users: IUser[][]) => this.users = users,
      (err: HttpErrorResponse) => {
        const error: IErrorResponse = this.exceptionService.errorHandling(err);
        this.openSnackBar(error.message, error.title);
      }
    );

    this.listSubscriptions = [...this.listSubscriptions,
      companiesSubscription, usersSubscription,
    ];
  }

  ngOnInit() {
    this.companyService.setSidenav(this.sidenav);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.listSubscriptions.map((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  private initializeDataset() {
    this.length = 0;
    this.rows = [];
    this.dataSource = new MatTableDataSource(this.rows);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  fetchData() {
    this.observable = this.mainService.getCurrentUser();
    const userSubscription: Subscription = this.observable.subscribe(
      (userResponse: IUser) => this.currentUser = userResponse
    );
    this.listSubscriptions = [...this.listSubscriptions, userSubscription, ];
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  addCompany() {
    this.router.navigate(['/home/companies/add']);
  }

  editRow(company: ICompany) {
    // validacion... cuando es login_as, el id cambia a pk, entonces se restablece!
    company['id'] = (company.hasOwnProperty('pk')) ? company['pk'] : company['id'];
    this.companyService.setCompany(company);
    this.router.navigate([`/home/companies/${company.id}`]);
  }

  openSnackBar(message: string, action: string): void {
    this.snackBar.open(message, action, {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      duration: 2000,
    });
  }

  showCompanyDetail(company: ICompany) {
    this.company = company;
  }

  private updateUsersState(company: ICompany) {
    const allUsers = Array.from(this.users[this.ROLES.ADMIN]).concat(Array.from(this.users[this.ROLES.CUSTOMER]));

    company.users.map((relationship) => {
      const relatedUser = relationship.user;
      const userFound: any = allUsers.find((user: IUser) => user.id === relatedUser.id);

      if (typeof userFound !== 'undefined') {
        const existingUser = Object.assign({}, userFound) as IUser;
        const objIndex = existingUser.companies.findIndex((rel: IUserCompany) => rel.company.id === company.id);
        if (objIndex >= 0) {existingUser.companies.splice(objIndex, 1); }
      }
    });
  }

  deleteCompany(company: ICompany): void {
    const params: IDialogData = {
      strQuestion: `¿Confirma la eliminación de ${company.name}?`,
      strAccept: 'Aceptar',
      strCancel: 'Cancelar',
      buttonColor: 'warn',
    };
    const dialogRef = this.dialog.open(DialogComponent, {
      width: '370px',
      data: params,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.companyService.closeSidenav();
        this.companyService.deleteCompany(company).subscribe(
          () => {
            this.updateUsersState(company);
            this.openSnackBar(`${company.name} ha sido borrada de la plataforma.`, 'info');
          },
          (err: HttpErrorResponse) => {
            const error: IErrorResponse = this.exceptionService.errorHandling(err);
            this.openSnackBar(error.message, error.title);
          }
        );
      }
    });
  }

  dataAdapter(companies: ICompany[]) {
    if (companies.length) {
      this.length = companies.length;
      this.rows = companies;
      this.dataSource = new MatTableDataSource(this.rows);
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    } else {
      this.initializeDataset();
    }
  }
}
